import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import clsx from "clsx";
import { format, isSameDay, parseISO } from "date-fns";

import {
  Avatar,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  TextField,
  Typography,
  makeStyles,
  useMediaQuery,
} from "@material-ui/core";
import AddCommentOutlinedIcon from "@material-ui/icons/AddCommentOutlined";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import GroupIcon from "@material-ui/icons/Group";
import MoreVertIcon from "@material-ui/icons/MoreVert";
import SendIcon from "@material-ui/icons/Send";

import MainContainer from "../../components/MainContainer";
import ConfirmationModal from "../../components/ConfirmationModal";
import InternalChatModal from "../../components/InternalChatModal";
import openSocket from "../../services/socket-io";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles((theme) => ({
  root: {
    flex: 1,
    minHeight: 0,
    display: "flex",
    gap: theme.spacing(2),
  },
  list: {
    width: 320,
    flexShrink: 0,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    borderRadius: theme.borderRadius.medium,
    [theme.breakpoints.down("xs")]: {
      width: "100%",
    },
  },
  listHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: theme.spacing(1.5, 2),
    borderBottom: theme.border.divider,
  },
  listBody: {
    flex: 1,
    overflowY: "auto",
    ...theme.scrollbarStyles,
  },
  chatItem: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    padding: theme.spacing(1.25, 2),
    cursor: "pointer",
    borderBottom: theme.border.divider,
    "&:hover": {
      backgroundColor: theme.palette.tintedBackground,
    },
  },
  chatItemActive: {
    backgroundColor: theme.palette.action.selected,
    "&:hover": {
      backgroundColor: theme.palette.action.selected,
    },
  },
  avatar: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
  },
  chatItemBody: {
    flex: 1,
    minWidth: 0,
  },
  chatItemTop: {
    display: "flex",
    justifyContent: "space-between",
    gap: theme.spacing(1),
  },
  muted: {
    color: theme.palette.text.secondary,
  },
  unread: {
    minWidth: 20,
    padding: "0 6px",
    borderRadius: theme.borderRadius.pill,
    backgroundColor: theme.palette.success.main,
    color: theme.palette.common.white,
    textAlign: "center",
    fontSize: 11,
    lineHeight: "20px",
    fontWeight: 600,
  },
  bold: {
    fontWeight: 600,
  },
  emptyList: {
    padding: theme.spacing(3),
    textAlign: "center",
    color: theme.palette.text.secondary,
    ...theme.typography.body2,
  },
  conversation: {
    flex: 1,
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    borderRadius: theme.borderRadius.medium,
  },
  conversationHeader: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    padding: theme.spacing(1, 2),
    borderBottom: theme.border.divider,
  },
  headerText: {
    flex: 1,
    minWidth: 0,
  },
  messages: {
    flex: 1,
    overflowY: "auto",
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.chat,
    ...theme.scrollbarStyles,
  },
  loadOlder: {
    display: "flex",
    justifyContent: "center",
    marginBottom: theme.spacing(1),
  },
  dayDivider: {
    margin: theme.spacing(1.5, 0),
    textAlign: "center",
    "& span": {
      padding: "2px 10px",
      borderRadius: theme.borderRadius.pill,
      backgroundColor: theme.palette.timestamp.background,
      ...theme.typography.caption,
    },
  },
  bubbleRow: {
    display: "flex",
    marginBottom: theme.spacing(0.75),
  },
  mine: {
    justifyContent: "flex-end",
  },
  bubble: {
    maxWidth: "70%",
    padding: theme.spacing(0.75, 1.25),
    borderRadius: 10,
    boxShadow: theme.elevation.light,
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    backgroundColor: theme.palette.contactMessage.main,
  },
  bubbleMine: {
    backgroundColor: theme.palette.userMessage.main,
  },
  sender: {
    display: "block",
    fontSize: 12,
    fontWeight: 600,
    color: theme.palette.primary.main,
  },
  bubbleTime: {
    display: "block",
    textAlign: "right",
    fontSize: 11,
    color: theme.palette.text.secondary,
  },
  composer: {
    display: "flex",
    alignItems: "flex-end",
    gap: theme.spacing(1),
    padding: theme.spacing(1, 2),
    borderTop: theme.border.divider,
  },
  placeholder: {
    flex: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(3),
    textAlign: "center",
    color: theme.palette.text.secondary,
  },
}));

const chatName = (chat, userId) =>
  chat.isGroup
    ? chat.title
    : chat.users.find((u) => u.id !== userId)?.name || i18n.t("internalChat.you");

const lastActivity = (chat) => chat.lastMessageAt || chat.createdAt;

const sortChats = (chats) =>
  [...chats].sort(
    (a, b) => new Date(lastActivity(b) || 0) - new Date(lastActivity(a) || 0)
  );

const InternalChat = () => {
  const classes = useStyles();
  const history = useHistory();
  const { chatId } = useParams();
  const { user } = useContext(AuthContext);
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("xs"));

  const [chats, setChats] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [messages, setMessages] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [page, setPage] = useState(1);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [modal, setModal] = useState({ open: false, chat: null });
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const messagesRef = useRef(null);
  const stickToBottom = useRef(true);

  const activeId = chatId ? Number(chatId) : null;
  const activeChat = chats.find((c) => c.id === activeId);

  const scrollToBottom = () => {
    const el = messagesRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  };

  const markRead = useCallback(async (id) => {
    try {
      await api.post(`/chats/${id}/read`);
    } catch (err) {
      // Sem impacto para o usuário: o contador só não zera agora
    }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/chats");
        setChats(data);
      } catch (err) {
        toastError(err);
      }
      setLoaded(true);
    })();
  }, []);

  useEffect(() => {
    setMessages([]);
    setPage(1);
    setHasMore(false);
    if (!activeId) return;
    (async () => {
      try {
        const { data } = await api.get(`/chats/${activeId}/messages`);
        setMessages(data.messages);
        setHasMore(data.hasMore);
        stickToBottom.current = true;
        setTimeout(scrollToBottom, 0);
        markRead(activeId);
      } catch (err) {
        toastError(err);
        history.push("/chats");
      }
    })();
  }, [activeId, history, markRead]);

  useEffect(() => {
    const socket = openSocket();

    socket.on("chat", (data) => {
      if (data.action === "delete") {
        setChats((prev) => prev.filter((c) => c.id !== data.chatId));
        if (data.chatId === activeId) history.push("/chats");
        return;
      }

      const { chat } = data;
      setChats((prev) => sortChats([chat, ...prev.filter((c) => c.id !== chat.id)]));

      if (data.action === "message" && chat.id === activeId) {
        setMessages((prev) =>
          prev.some((m) => m.id === data.message.id) ? prev : [...prev, data.message]
        );
        if (data.message.senderId !== user.id) markRead(chat.id);
      }
    });

    return () => socket.disconnect();
  }, [activeId, history, markRead, user.id]);

  useEffect(() => {
    if (stickToBottom.current) scrollToBottom();
  }, [messages]);

  const handleScroll = () => {
    const el = messagesRef.current;
    stickToBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
  };

  const loadOlder = async () => {
    const next = page + 1;
    const el = messagesRef.current;
    const previousHeight = el.scrollHeight;
    try {
      const { data } = await api.get(`/chats/${activeId}/messages`, {
        params: { pageNumber: next },
      });
      stickToBottom.current = false;
      setMessages((prev) => [...data.messages, ...prev]);
      setHasMore(data.hasMore);
      setPage(next);
      // Mantém a posição de leitura depois de inserir mensagens acima
      setTimeout(() => {
        el.scrollTop = el.scrollHeight - previousHeight;
      }, 0);
    } catch (err) {
      toastError(err);
    }
  };

  const handleSend = async () => {
    const body = text.trim();
    if (!body || sending) return;
    setSending(true);
    try {
      const { data } = await api.post(`/chats/${activeId}/messages`, { body });
      stickToBottom.current = true;
      setMessages((prev) => (prev.some((m) => m.id === data.id) ? prev : [...prev, data]));
      setText("");
    } catch (err) {
      toastError(err);
    }
    setSending(false);
  };

  const handleRemove = async () => {
    try {
      await api.delete(`/chats/${activeId}`);
      setChats((prev) => prev.filter((c) => c.id !== activeId));
      history.push("/chats");
    } catch (err) {
      toastError(err);
    }
  };

  const canManage =
    activeChat?.isGroup &&
    (user.profile === "admin" || activeChat.ownerId === user.id);

  const showList = !isMobile || !activeId;
  const showConversation = !isMobile || !!activeId;

  const renderAvatar = (chat) => (
    <Avatar className={classes.avatar}>
      {chat.isGroup ? <GroupIcon /> : chatName(chat, user.id).charAt(0)}
    </Avatar>
  );

  return (
    <MainContainer>
      <InternalChatModal
        open={modal.open}
        chat={modal.chat}
        onClose={() => setModal({ open: false, chat: null })}
        onSaved={(chat) => {
          setChats((prev) => sortChats([chat, ...prev.filter((c) => c.id !== chat.id)]));
          history.push(`/chats/${chat.id}`);
        }}
      />
      <ConfirmationModal
        title={i18n.t(canManage ? "internalChat.deleteGroup" : "internalChat.leave")}
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleRemove}
      >
        {i18n.t(canManage ? "internalChat.confirmDelete" : "internalChat.confirmLeave")}
      </ConfirmationModal>

      <div className={classes.root}>
        {showList && (
          <Paper variant="outlined" className={classes.list}>
            <div className={classes.listHeader}>
              <Typography variant="h6" color="primary">
                {i18n.t("internalChat.title")}
              </Typography>
              <IconButton
                color="primary"
                title={i18n.t("internalChat.newChat")}
                onClick={() => setModal({ open: true, chat: null })}
              >
                <AddCommentOutlinedIcon />
              </IconButton>
            </div>
            <div className={classes.listBody}>
              {loaded && chats.length === 0 && (
                <div className={classes.emptyList}>{i18n.t("internalChat.noChats")}</div>
              )}
              {chats.map((chat) => {
                const at = lastActivity(chat) && parseISO(lastActivity(chat));
                return (
                  <div
                    key={chat.id}
                    className={clsx(
                      classes.chatItem,
                      chat.id === activeId && classes.chatItemActive
                    )}
                    onClick={() => history.push(`/chats/${chat.id}`)}
                  >
                    {renderAvatar(chat)}
                    <div className={classes.chatItemBody}>
                      <div className={classes.chatItemTop}>
                        <Typography
                          variant="body2"
                          noWrap
                          className={clsx(chat.unreads > 0 && classes.bold)}
                        >
                          {chatName(chat, user.id)}
                        </Typography>
                        {at && (
                          <Typography variant="caption" className={classes.muted}>
                            {format(at, isSameDay(at, new Date()) ? "HH:mm" : "dd/MM")}
                          </Typography>
                        )}
                      </div>
                      <div className={classes.chatItemTop}>
                        <Typography variant="caption" noWrap className={classes.muted}>
                          {chat.lastMessage || " "}
                        </Typography>
                        {chat.unreads > 0 && (
                          <span className={classes.unread}>
                            {chat.unreads > 99 ? "99+" : chat.unreads}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </Paper>
        )}

        {showConversation && (
          <Paper variant="outlined" className={classes.conversation}>
            {!activeChat ? (
              <div className={classes.placeholder}>{i18n.t("internalChat.selectChat")}</div>
            ) : (
              <>
                <div className={classes.conversationHeader}>
                  {isMobile && (
                    <IconButton size="small" onClick={() => history.push("/chats")}>
                      <ArrowBackIcon />
                    </IconButton>
                  )}
                  {renderAvatar(activeChat)}
                  <div className={classes.headerText}>
                    <Typography variant="subtitle1" noWrap>
                      {chatName(activeChat, user.id)}
                    </Typography>
                    {activeChat.isGroup && (
                      <Typography variant="caption" noWrap component="div" className={classes.muted}>
                        {activeChat.users.map((u) => u.name).join(", ")}
                      </Typography>
                    )}
                  </div>
                  <IconButton onClick={(e) => setMenuAnchor(e.currentTarget)}>
                    <MoreVertIcon />
                  </IconButton>
                  <Menu
                    anchorEl={menuAnchor}
                    open={!!menuAnchor}
                    onClose={() => setMenuAnchor(null)}
                  >
                    {canManage && (
                      <MenuItem
                        onClick={() => {
                          setMenuAnchor(null);
                          setModal({ open: true, chat: activeChat });
                        }}
                      >
                        {i18n.t("internalChat.editGroup")}
                      </MenuItem>
                    )}
                    <MenuItem
                      onClick={() => {
                        setMenuAnchor(null);
                        setConfirmOpen(true);
                      }}
                    >
                      {i18n.t(canManage ? "internalChat.deleteGroup" : "internalChat.leave")}
                    </MenuItem>
                  </Menu>
                </div>

                <div className={classes.messages} ref={messagesRef} onScroll={handleScroll}>
                  {hasMore && (
                    <div className={classes.loadOlder}>
                      <Button size="small" onClick={loadOlder}>
                        {i18n.t("internalChat.loadOlder")}
                      </Button>
                    </div>
                  )}
                  {messages.map((message, index) => {
                    const at = parseISO(message.createdAt);
                    const previous = messages[index - 1];
                    const newDay = !previous || !isSameDay(parseISO(previous.createdAt), at);
                    const mine = message.senderId === user.id;
                    return (
                      <React.Fragment key={message.id}>
                        {newDay && (
                          <div className={classes.dayDivider}>
                            <span>{format(at, "dd/MM/yyyy")}</span>
                          </div>
                        )}
                        <div className={clsx(classes.bubbleRow, mine && classes.mine)}>
                          <div className={clsx(classes.bubble, mine && classes.bubbleMine)}>
                            {activeChat.isGroup && !mine && (
                              <span className={classes.sender}>{message.sender?.name}</span>
                            )}
                            {message.body}
                            <span className={classes.bubbleTime}>{format(at, "HH:mm")}</span>
                          </div>
                        </div>
                      </React.Fragment>
                    );
                  })}
                </div>

                <div className={classes.composer}>
                  <TextField
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        handleSend();
                      }
                    }}
                    placeholder={i18n.t("internalChat.placeholder")}
                    variant="outlined"
                    size="small"
                    multiline
                    maxRows={5}
                    fullWidth
                    inputProps={{ maxLength: 4000 }}
                  />
                  <IconButton
                    color="primary"
                    disabled={!text.trim() || sending}
                    onClick={handleSend}
                    title={i18n.t("internalChat.send")}
                  >
                    <SendIcon />
                  </IconButton>
                </div>
              </>
            )}
          </Paper>
        )}
      </div>
    </MainContainer>
  );
};

export default InternalChat;
